import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '../utils/animations';
import '../styles/CookingSchool.css';

const CookingSchool = () => {
  return (
    <div className="cooking-school-page">
      <div className="container">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="cooking-school-header"
        >
          <h1>Főzőiskola</h1>
          <p className="subtitle">
            Tippek, trükkök és alapok kezdőknek és haladóknak
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="cooking-school-content"
        >
          {/* Alapfogalmak szekció */}
          <section className="cooking-section card">
            <h2>🔪 Alapfogalmak</h2>
            <div className="technique-grid">
              <div className="technique-item">
                <h3>Rántás</h3>
                <p>
                  A rántás a magyar konyha alapja. Zsíron (olaj, vaj, zsír)
                  hevítünk lisztet, amíg aranybarna színt nem kap. A rántás
                  sűrítésre, ízesítésre és színezésre szolgál. Fontos, hogy
                  folyamatosan keverjük, nehogy megégjen.
                </p>
                <ul>
                  <li>Fehér rántás: csak hevítjük, nem barnítjuk</li>
                  <li>Világos rántás: enyhén megbarnítjuk</li>
                  <li>Barna rántás: erősen megbarnítjuk (pl. pörkölt)</li>
                </ul>
              </div>

              <div className="technique-item">
                <h3>Habarás</h3>
                <p>
                  Tojássárgáját keverünk össze tejföllel vagy tejjel, hogy
                  krémes, sima masszát kapjunk. Ezt hozzáadjuk levesekhez,
                  mártásokhoz sűrítésre és krémesítésre. Fontos, hogy ne
                  forralja fel, mert kicsapódik!
                </p>
                <div className="tip">
                  💡 Tipp: Mindig keverjünk egy kis forró levet a habarásba
                  először (temperálás), hogy ne csapódjon ki.
                </div>
              </div>

              <div className="technique-item">
                <h3>Szuvidálás (Sous-vide)</h3>
                <p>
                  Modern főzési technika, ahol az ételt vákuumcsomagolva,
                  pontos hőmérsékleten, vízfürdőben készítjük el. Előnye,
                  hogy az étel nem szárad ki, és tökéletesen egyenletes
                  az átsülés.
                </p>
                <ul>
                  <li>Steak: 54-57°C (medium-rare)</li>
                  <li>Csirkemell: 63-65°C</li>
                  <li>Tojás: 63°C (onsen egg)</li>
                </ul>
              </div>

              <div className="technique-item">
                <h3>Pirítás (Searing)</h3>
                <p>
                  Nagy hőn, rövid ideig sütjük az ételt, hogy karamellizált
                  kéreg képződjön rajta. Ez ízt és színt ad. Fontos, hogy a
                  serpenyő és az olaj is forró legyen.
                </p>
              </div>

              <div className="technique-item">
                <h3>Pácólás (Marinálás)</h3>
                <p>
                  Fűszerekkel, savakkal (citrom, ecet, bor) és olajjal
                  ízesítjük és puhítjuk a húst. Ideális pácolási idő:
                  2-24 óra a hűtőben.
                </p>
              </div>

              <div className="technique-item">
                <h3>Redukálás</h3>
                <p>
                  Sűrítési technika, ahol a folyadékot (bor, alap, mártás)
                  forraljuk, hogy elpárologjon belőle a víz, és sűrűbb,
                  koncentráltabb ízű legyen.
                </p>
              </div>
            </div>
          </section>

          {/* Konyhatechnikai tippek */}
          <section className="cooking-section card">
            <h2>👨‍🍳 Konyhatechnikai Tippek</h2>
            <div className="tips-list">
              <div className="tip-card">
                <span className="tip-icon">🧂</span>
                <div>
                  <h4>Sózz rétegenként</h4>
                  <p>
                    Ne csak a végén sózd meg az ételt, hanem minden rétegnél
                    (pl. hagyma pirítás, hús, zöldségek). Így mélyebb,
                    kiegyensúlyozottabb ízt érsz el.
                  </p>
                </div>
              </div>

              <div className="tip-card">
                <span className="tip-icon">🔥</span>
                <div>
                  <h4>Melegítsd elő a serpenyőt</h4>
                  <p>
                    Mindig forró serpenyőre tedd a húst vagy zöldséget.
                    Így nem fog hozzáragadni, és szép pirítást kapsz.
                  </p>
                </div>
              </div>

              <div className="tip-card">
                <span className="tip-icon">🧊</span>
                <div>
                  <h4>Szobahőmérsékletű alapanyagok</h4>
                  <p>
                    A húst vedd ki 30 perccel főzés előtt a hűtőből.
                    Így egyenletesebben sül át.
                  </p>
                </div>
              </div>

              <div className="tip-card">
                <span className="tip-icon">🥄</span>
                <div>
                  <h4>Kóstolj, kóstolj, kóstolj!</h4>
                  <p>
                    A főzés közben folyamatosan kóstold meg az ételt.
                    Így tudod pontosan beállítani az ízeket.
                  </p>
                </div>
              </div>

              <div className="tip-card">
                <span className="tip-icon">🔪</span>
                <div>
                  <h4>Éles kés = biztonság</h4>
                  <p>
                    Egy éles késsel sokkal biztonságosabb dolgozni, mint
                    egy tompa késsel, ami megcsúszik. Élezd rendszeresen!
                  </p>
                </div>
              </div>

              <div className="tip-card">
                <span className="tip-icon">⏱️</span>
                <div>
                  <h4>Pihentetés</h4>
                  <p>
                    Sült húst mindig pihentess 5-10 percig, mielőtt
                    felvágod. Így a nedvek visszaoszlanak, és nem folyik
                    ki belőle.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Alapreceptek */}
          <section className="cooking-section card">
            <h2>📖 Alapreceptek</h2>
            <div className="base-recipes">
              <div className="base-recipe-item">
                <h3>Alaplé (Stock)</h3>
                <p>
                  Zöldségek, csontok vagy halak hosszú ideig főzve készítünk
                  ízletes alapot, amit levesekhez, mártásokhoz használunk.
                </p>
                <div className="recipe-types">
                  <span className="badge">Csirkealap</span>
                  <span className="badge">Marhaalap</span>
                  <span className="badge">Zöldségalap</span>
                  <span className="badge">Halalap</span>
                </div>
              </div>

              <div className="base-recipe-item">
                <h3>Vinaigrette</h3>
                <p>
                  Egyszerű salátaöntet: 3 rész olaj + 1 rész ecet/citrom +
                  só + bors + opcionális méz/mustár.
                </p>
              </div>

              <div className="base-recipe-item">
                <h3>Pesto</h3>
                <p>
                  Bazsalikom + fokhagyma + parmezán + piniónmag + olívaolaj
                  mixelve. Tésztákhoz, szendvicsekhez, húsokhoz.
                </p>
              </div>

              <div className="base-recipe-item">
                <h3>Béchamell mártás</h3>
                <p>
                  Fehér rántás + tej + só + bors + szerecsendió. Alapja
                  sok más mártásnak (pl. sajtos Mornay mártás).
                </p>
              </div>
            </div>
          </section>

          {/* Alapanyag-ismeret */}
          <section className="cooking-section card">
            <h2>🥩 Alapanyag-ismeret</h2>
            <div className="ingredient-guide">
              <div className="ingredient-item">
                <h3>Hús hőmérséklet</h3>
                <ul>
                  <li>Rare (véres): 52-55°C</li>
                  <li>Medium rare: 55-60°C</li>
                  <li>Medium: 60-65°C</li>
                  <li>Medium well: 65-69°C</li>
                  <li>Well done (átsütött): 70°C+</li>
                  <li>Csirke/sertés: minimum 75°C</li>
                </ul>
              </div>

              <div className="ingredient-item">
                <h3>Olajok füstpontja</h3>
                <ul>
                  <li>Extra szűz olívaolaj: 160-190°C (alacsony hő)</li>
                  <li>Vaj: 150°C (alacsony hő)</li>
                  <li>Szezámolaj: 177°C (közepes hő)</li>
                  <li>Kókuszolaj: 177°C (közepes hő)</li>
                  <li>Napraforgóolaj: 225°C (magas hő)</li>
                  <li>Avokádóolaj: 270°C (nagyon magas hő)</li>
                </ul>
              </div>

              <div className="ingredient-item">
                <h3>Fűszer párosítások</h3>
                <p>
                  <strong>Csirkéhez:</strong> rozmaring, kakukkfű, oregánó,
                  citrom, fokhagyma<br/>
                  <strong>Marhához:</strong> rozmaring, kakukkfű, bors,
                  fokhagyma, pirospaprika<br/>
                  <strong>Halhoz:</strong> kapor, citrom, fehérbor, petrezselyem<br/>
                  <strong>Tészta:</strong> bazsalikom, oregánó, fokhagyma, paradicsom
                </p>
              </div>
            </div>
          </section>

          {/* Hasznos eszközök */}
          <section className="cooking-section card">
            <h2>🔧 Hasznos Eszközök</h2>
            <div className="tools-list">
              <div className="tool-item">
                <h4>✓ Konyhai hőmérő</h4>
                <p>Elengedhetetlen a pontos hús sütéshez.</p>
              </div>
              <div className="tool-item">
                <h4>✓ Jó minőségű kések</h4>
                <p>Chef's knife, szeletelő kés, kis zöldségvágó.</p>
              </div>
              <div className="tool-item">
                <h4>✓ Öntöttvas serpenyő</h4>
                <p>Egyenletes hőeloszlás, tartós, sok célra használható.</p>
              </div>
              <div className="tool-item">
                <h4>✓ Konyhai mérleg</h4>
                <p>Pontos méréshez, főleg sütéshez.</p>
              </div>
              <div className="tool-item">
                <h4>✓ Jó vágódeszka</h4>
                <p>Fa vagy műanyag, stabil és nagy felületű.</p>
              </div>
              <div className="tool-item">
                <h4>✓ Mixológép / botmixer</h4>
                <p>Levesek, mártások, smoothie-k készítéshez.</p>
              </div>
            </div>
          </section>

          {/* Gyors tippek */}
          <section className="cooking-section card quick-tips">
            <h2>⚡ Gyors Tippek</h2>
            <ul className="quick-tips-list">
              <li>🧄 <strong>Fokhagyma illat a kezeden?</strong> Dörzsöld rozsdamentes acél felülethez (pl. csap) hideg víz alatt.</li>
              <li>🥚 <strong>Tojás frissesség tesztelése:</strong> Tedd vízbe - ha elsüllyed, friss; ha lebeg, nem.</li>
              <li>🥑 <strong>Érett avokádó?</strong> Nyomd meg: ha kicsit enged, érett. Ha nagyon puha, túlérett.</li>
              <li>🍋 <strong>Több citromlé:</strong> Gördítsd le erősen az asztalon a facsarás előtt.</li>
              <li>🧅 <strong>Hagymavágás könnyezés nélkül:</strong> Tedd a fagyasztóba 15 percre, vagy vágd hideg víz alatt.</li>
              <li>🥔 <strong>Ropogós sült krumpli:</strong> Főzd le 10 percig először, majd süsd.</li>
              <li>🧈 <strong>Hideg vaj olvasztása:</strong> Reszeld le - gyorsabban olvad.</li>
              <li>🥗 <strong>Crispy saláta:</strong> Áztasd jéghideg vízben 30 percig.</li>
            </ul>
          </section>

          {/* Call to action */}
          <section className="cta-section">
            <h2>Készen állsz a főzésre?</h2>
            <p>Fedezd fel receptjeinket, és gyakorold a technikákat!</p>
            <a href="/recipes" className="btn btn-primary btn-lg">
              Böngészd a recepteket
            </a>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default CookingSchool;
